import React, { useRef, useState, useEffect } from 'react'
import {
  Card,
  CardMedia,
  Typography,
  Container,
  Button,
  Grid,
  Box,
  CardContent,
  CardHeader,
  Avatar,
  Skeleton,
  Alert,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { IApplicationState } from 'Store/types'
import {
  addVehicleVisitingPoint,
  removeVehicleVisitingPoint,
  addRecordedChunk,
  clearVehicleVisitingPoints,
  clearVideo,
  setVideoRecorded,
  setCurrentImage,
  setMode,
} from '../../Actions/reducer'
import { useTranslation } from 'react-i18next'
import { getVehicleVisitingPoints } from '../../Actions/action'
import { useAppDispatch } from 'Hooks/reduxHooks'
import Const from 'Utils/Consts'
import { format } from 'react-string-format'
import VisitImage from 'Components/Image/VisitImage'
import VisitsApiPaths from 'Application/Visits/Constants/VisitsApiPaths'
import Icon from 'Components/Icon'
import CircularProgressbar from 'Components/CircularProgressbar'
import { useSettings } from 'Contexts/SettingsContext'

const VehicleVisitingPointsMode2Page = (props: {
  handleNext: () => void
  handleBack: () => void
}) => {
  const [t] = useTranslation()
  const { settings } = useSettings()
  const width =
    settings?.REACT_APP_WIDTH != undefined ? settings?.REACT_APP_WIDTH : Const.env.REACT_APP_WIDTH
  const height =
    settings?.REACT_APP_HEIGHT != undefined
      ? settings?.REACT_APP_HEIGHT
      : Const.env.REACT_APP_HEIGHT
  const quality =
    settings?.REACT_APP_IMAGE_QUALITY != undefined
      ? settings?.REACT_APP_IMAGE_QUALITY
      : Const.env.REACT_APP_IMAGE_QUALITY
  const videoFormat =
    settings?.REACT_APP_VIDEO_FORMAT != undefined
      ? settings?.REACT_APP_VIDEO_FORMAT
      : Const.env.REACT_APP_VIDEO_FORMAT
  const frameRate =
    settings?.REACT_APP_VIDEO_FRAME_RATE != undefined
      ? settings?.REACT_APP_VIDEO_FRAME_RATE
      : Const.env.REACT_APP_VIDEO_FRAME_RATE
  const videoMaxSize =
    settings?.REACT_APP_VIDEO_MAX_TIME != undefined
      ? settings?.REACT_APP_VIDEO_MAX_TIME
      : Const.env.REACT_APP_VIDEO_MAX_TIME
  const { data, videoRecorded, currentImage, mode } = useSelector(
    (store: IApplicationState) => store.visits.newVisit.vehicleVisitingPoints,
  )
  const { vehicleVisitingPoints } = useSelector((store: IApplicationState) => store.visits)
  const dispatch = useAppDispatch()
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment')
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null)
  const [videoRecorder, setVideoRecorder] = useState<MediaRecorder | null>(null)
  const [clearForceUpdate, setClearForceUpdate] = useState<number>(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [cameraStatus, setCameraStatus] = useState<boolean>(false)
  const [videoRecording, setVideoRecording] = useState<boolean>(false)
  const [imageCaptured, setImageCaptured] = useState<boolean>(false)
  const [videoTime, setVideoTime] = useState<number>(videoMaxSize)
  const [alertTime, setAlertTime] = useState<number>(0)

  useEffect(() => {
    if (videoRecording) {
      if (videoTime < videoMaxSize) {
        const interval = setInterval(() => {
          setVideoTime(prev => prev + 1)
        }, 1000)

        return () => {
          clearInterval(interval)
        }
      } else {
        if (videoRecorder) {
          videoRecorder.stop()
          setVideoRecording(false)
          dispatch(setVideoRecorded(true))
          dispatch(setMode('Finished'))
          setCameraStatus(false)
          if (videoStream) videoStream?.getTracks()?.forEach(track => track?.stop())
        }
      }
    }
  }, [videoTime])

  useEffect(() => {
    let stream: MediaStream
    if (cameraStatus == false && mode != 'Finished') {
      const getCameraStream = async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: width,
              height: height,
              frameRate: frameRate,
              facingMode: facingMode,
            },
            audio: true,
          })

          if (videoRef.current) {
            videoRef.current.srcObject = stream
            if (!videoRef.current.played) videoRef.current.play()
          }

          setVideoStream(stream)
          const recorder = new MediaRecorder(stream, { mimeType: videoFormat })
          recorder.ondataavailable = (event: BlobEvent) => {
            dispatch(addRecordedChunk(event.data))
          }
          setVideoRecorder(recorder)
          setCameraStatus(true)
        } catch (error) {
          console.error('Error accessing the camera', error)
        }
      }

      if (
        !vehicleVisitingPoints.vehicleVisitingPointsLoaded &&
        !vehicleVisitingPoints.vehicleVisitingPointsLoading
      ) {
        dispatch(getVehicleVisitingPoints()).then((res: any) => {
          getCameraStream()
        })
      } else {
        getCameraStream()
      }
    }

    return () => {
      stream?.getTracks()?.forEach(track => track?.stop())
    }
  }, [facingMode, clearForceUpdate, vehicleVisitingPoints.vehicleVisitingPointsLoaded])

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      if (mode == 'Image') {
        const context = canvasRef.current.getContext('2d')
        if (context) {
          canvasRef.current.width = width
          canvasRef.current.height = height
          context.drawImage(
            videoRef.current,
            0,
            0,
            canvasRef.current.width,
            canvasRef.current.height,
          )
          dispatch(
            addVehicleVisitingPoint({
              id: vehicleVisitingPoints.data[currentImage]?.value,
              name: vehicleVisitingPoints.data[currentImage]?.label,
              isHealthy: true,
              image: canvasRef.current.toDataURL('image/png', quality),
              fileType: 'image/png',
              status: 0,
              uploadProgress: 0,
              isPlatePhotoAvailable:
                vehicleVisitingPoints.data[currentImage]?.isPlatePhotoAvailable,
            }),
          )
          for (let i = 0; i < vehicleVisitingPoints.data.length; i++) {
            if (i != currentImage) {
              let item = data.find(x => x.id == vehicleVisitingPoints.data[i]?.value)
              if (item == undefined) {
                dispatch(setCurrentImage(i))
                return
              }
            }
          }
          setImageCaptured(true)
          if (!videoRecorded) dispatch(setMode('Video'))
          else {
            dispatch(setMode('Finished'))
            setCameraStatus(false)
            if (videoStream) {
              videoStream?.getTracks()?.forEach(track => track?.stop())
            }
          }
        }
      } else if (mode == 'Video') {
        if (videoRecorder) {
          if (!videoRecording) {
            videoRecorder.start()
            setVideoRecording(true)
            setVideoTime(0)
          } else if (videoMaxSize - videoTime < videoMaxSize / 2) {
            videoRecorder.stop()
            setVideoRecording(false)
            dispatch(setVideoRecorded(true))
            dispatch(setMode('Finished'))
            setCameraStatus(false)
            if (videoStream) videoStream?.getTracks()?.forEach(track => track?.stop())
          } else {
            setAlertTime(6)
          }
        } else if (Const.env.REACT_APP_VIDEO_MOCK) {
          if (!videoRecording) {
            setVideoRecording(true)
            setVideoTime(0)
          } else if (videoMaxSize - videoTime < videoMaxSize / 2) {
            setVideoRecording(false)
            dispatch(setVideoRecorded(true))
            dispatch(setMode('Finished'))
            setCameraStatus(false)
          } else {
            setAlertTime(6)
          }
        }
      }
    }
  }

  const clearVehicleVisitingPointsClickHandler = () => {
    dispatch(clearVehicleVisitingPoints())
    dispatch(setCurrentImage(0))
    dispatch(setMode('Image'))
    setImageCaptured(false)
    setClearForceUpdate(prev => prev + 1)
  }

  const clearVideoClickHandler = () => {
    dispatch(clearVideo())
    dispatch(setMode('Video'))
    setClearForceUpdate(prev => prev + 1)
  }

  const handleNextButton = () => {
    if (imageCaptured && videoRecorded) props.handleNext()
  }

  const handleDeleteClick = (id: number, index: number) => {
    dispatch(removeVehicleVisitingPoint(id))
    dispatch(setCurrentImage(index))
    if (mode != 'Image') dispatch(setMode('Image'))
  }

  const handleImageCapturingFinishClick = () => {
    if (checkAllRequirdCaptured()) {
      setImageCaptured(true)
      if (!videoRecorded) dispatch(setMode('Video'))
      else {
        dispatch(setMode('Finished'))
        setCameraStatus(false)
        if (videoStream) {
          videoStream?.getTracks()?.forEach(track => track?.stop())
        }
      }
    }
  }

  const checkAllRequirdCaptured = (): boolean => {
    let isCaptured = true
    vehicleVisitingPoints.data.map(v => {
      if (v.isRequird) {
        isCaptured = isCaptured && data.findIndex(x => x.id == v.value) > -1
      }
    })
    return isCaptured
  }

  const Images = (): any => {
    let imageList: any[] = []
    vehicleVisitingPoints.data.map((vehicleVisitingPoint, i) => {
      let item = data.find(x => x.id == vehicleVisitingPoint.value)
      if (item) {
        imageList.push(
          <Card
            key={item.id}
            style={{
              minWidth: 100,
              maxWidth: 100,
              margin: '0 5px',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {item.image ? (
              <CardMedia component="img" image={item.image} style={{ height: '100%' }} />
            ) : (
              <VisitImage
                url={`${Const.env.REACT_APP_API_GATEWAY_URL}${VisitsApiPaths.DownloadThumbnailFile}${item.fileId}`}
                style={{ height: '100%' }}
              />
            )}
            {mode == 'Image' && (
              <Icon
                name="DeleteOutline"
                style={{ position: 'absolute', top: 0, left: 0, color: 'gray' }}
                onClick={() => handleDeleteClick(Number(item?.id), i)}
              />
            )}
            <Typography
              variant="subtitle2"
              fontSize={10}
              style={{ position: 'absolute', top: 0, right: 0 }}
            >
              {t(vehicleVisitingPoint.isRequird ? 'REQUIRD' : 'UN_REQUIRD')}
            </Typography>
            <Typography
              variant="subtitle2"
              textAlign="center"
              style={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                background: 'rgba(0, 0, 0, 0.3)',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {item.name}
            </Typography>
          </Card>,
        )
      } else {
        imageList.push(
          <Card
            key={vehicleVisitingPoint?.value}
            style={{
              minWidth: 100,
              maxWidth: 100,
              margin: '0 5px',
              cursor: 'pointer',
              position: 'relative',
            }}
            onClick={() => dispatch(setCurrentImage(i))}
          >
            <CardMedia
              component="div"
              style={{
                height: 80,
                backgroundColor: i == currentImage ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Icon name="PhotoCamera" style={{ fontSize: 30, color: 'gray' }} />
            </CardMedia>
            <Typography
              variant="subtitle2"
              fontSize={10}
              style={{ position: 'absolute', top: 0, right: 0 }}
            >
              {t(vehicleVisitingPoint.isRequird ? 'REQUIRD' : 'UN_REQUIRD')}
            </Typography>
            <Typography
              variant="subtitle2"
              textAlign="center"
              style={{
                position: 'absolute',
                bottom: 0,
                width: '100%',
                background: 'rgba(0, 0, 0, 0.3)',
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              {vehicleVisitingPoint?.label}
            </Typography>
          </Card>,
        )
      }
    })
    return imageList
  }

  return (
    <Card square={true} variant="outlined" sx={{ width: 1, paddingTop: '8px', paddingBottom: 8 }}>
      <CardContent sx={{ padding: '5px !important' }}>
        <CardHeader
          sx={{ padding: 0, marginBottom: '15px' }}
          avatar={
            <Avatar
              sx={{ bgcolor: '#2196f3', width: 60, height: 60 }}
              alt={t('VEHICLE_VISITING_POINTS')}
            >
              <Icon name="DirectionsCar" style={{ fontSize: 30, color: 'white' }} />
            </Avatar>
          }
          title={
            <Typography component="span" variant="h6">
              {t('VEHICLE_VISITING_POINTS')}
            </Typography>
          }
          subheader={
            <Typography component="div" gutterBottom variant="subtitle2">
              {''}
            </Typography>
          }
        />

        <div
          style={{
            marginTop: 20,
            padding: 3,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            display: 'flex',
          }}
        >
          <Images />
        </div>
      </CardContent>

      <Container>
        {vehicleVisitingPoints.data.length == 0 ? (
          <React.Fragment>
            <Skeleton width={'100%'} height={100} />
            <Skeleton width={'100%'} height={300} />
          </React.Fragment>
        ) : (
          <React.Fragment>
            {mode != 'Image' && (
              <Box textAlign="center" sx={{ mb: 5, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Icon name="DeleteOutline" />}
                  sx={{ mr: 1 }}
                  onClick={clearVehicleVisitingPointsClickHandler}
                >
                  {t('CLEAR_AUTO_PARTS')}
                </Button>
                {mode == 'Finished' && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Icon name="DeleteOutline" />}
                    onClick={clearVideoClickHandler}
                  >
                    {t('CLEAR_VIDEO')}
                  </Button>
                )}
              </Box>
            )}
            {mode != 'Finished' ? (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <div style={{ position: 'relative' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={handleCapture}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                  <Typography
                    variant="body2"
                    style={{
                      position: 'absolute',
                      top: 0,
                      width: '100%',
                      padding: 10,
                      background: 'rgba(0, 0, 0, 0.3)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    {t(
                      mode == 'Image'
                        ? 'CAPTURE'
                        : videoRecording
                          ? 'STOP_CAPTURE_VIDEO'
                          : 'CAPTURE_VIDEO',
                    )}
                  </Typography>
                  {mode == 'Image' && (
                    <Typography
                      variant="body2"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        width: '100%',
                        padding: 10,
                        background: 'rgba(0, 0, 0, 0.3)',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      {vehicleVisitingPoints.data[currentImage]?.label}
                    </Typography>
                  )}
                  <Icon
                    name="Cameraswitch"
                    onClick={() =>
                      setFacingMode(facingMode == 'environment' ? 'user' : 'environment')
                    }
                    style={{
                      fontSize: 25,
                      position: 'absolute',
                      top: 7,
                      right: 10,
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  />
                  {mode == 'Video' && videoRecording && (
                    <React.Fragment>
                      <Icon
                        name="Videocam"
                        onClick={() =>
                          setFacingMode(facingMode == 'environment' ? 'user' : 'environment')
                        }
                        style={{
                          fontSize: 25,
                          position: 'absolute',
                          top: 7,
                          left: 10,
                          color: 'rgba(255, 0, 0, 0.7)',
                        }}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="40px"
                        position="absolute"
                        top={0}
                        right={40}
                      >
                        <Box position="relative" display="inline-flex">
                          <CircularProgressbar
                            variant="determinate"
                            value={((videoMaxSize - videoTime) / videoMaxSize) * 100}
                            size={28}
                          />
                          <Box
                            top={2}
                            left={0}
                            bottom={0}
                            right={0}
                            position="absolute"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Typography
                              variant="subtitle2"
                              textAlign="center"
                              style={{ color: 'rgba(255, 255, 255, 0.7)' }}
                            >
                              {videoMaxSize - videoTime}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  )}
                </div>
              </div>
            ) : (
              ''
            )}
          </React.Fragment>
        )}
        {mode == 'Video' && (
          <Alert variant="filled" severity="warning" sx={{ mt: 1 }}>
            {format(t('VIDEO_MESSAGE'), videoMaxSize)}
          </Alert>
        )}
        {alertTime != 0 && (
          <Alert variant="filled" severity="error" sx={{ mt: 1 }}>
            {format(t('VALIDATION_ERROR_VIDEO_SMALL'), videoMaxSize / 2)}
          </Alert>
        )}
        <Grid container sx={{ mt: 2 }}>
          {mode == 'Image' && checkAllRequirdCaptured() ? (
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="warning"
                startIcon={<Icon name="KeyboardArrowRight" />}
                fullWidth
                onClick={handleImageCapturingFinishClick}
              >
                {t('IMAGE_CAPTURING_FINISH')}
              </Button>
            </Grid>
          ) : (
            <React.Fragment>
              <Grid item xs={6} sx={{ pr: 1 }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Icon name="KeyboardArrowRight" />}
                  fullWidth
                  onClick={props.handleBack}
                  disabled={(data.length > 0 && !imageCaptured) || videoRecording}
                >
                  {t('BACK')}
                </Button>
              </Grid>
              <Grid item xs={6} sx={{ pl: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  endIcon={<Icon name="KeyboardArrowLeft" />}
                  fullWidth
                  disabled={mode != 'Finished'}
                  onClick={handleNextButton}
                >
                  {t('NEXT')}
                </Button>
              </Grid>
            </React.Fragment>
          )}
        </Grid>
      </Container>
    </Card>
  )
}

export default VehicleVisitingPointsMode2Page
